import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { PrimaryBtn } from '../../../styles/ds'

import { useApi } from '../../../hooks/useApi'
import { useEnvironmentConfig } from '../../../hooks/useEnvironmentConfig'
import Accordion from '../../general/Accordion'
import TimeLockEdit from './components/TimeLockEdit'
import DefaultTokenThresholdEdit from './components/DefaultTokenThresholdEdit'
import DefaultVolumeLimitEdit from './components/DefaultVolumeLimitEdit'
import CustomConfirmationModal from '../../CustomConfirmationModal'
import DiffViewer, { DiffMethod } from 'react-diff-viewer'

interface Task {
  id: string
  name: string
  taskConfig: {
    timeLock: {
      id: string
      mode: string
      allowedAt: string
      frequency: string
      window: string
    }
    defaultTokenThreshold: {
      id: string
      max: string
      min: string
      token: {
        id: string
        name: string
        decimals: string
        symbol: string
      }
    }
    defaultVolumeLimit: {
      id: string
      amount: string
      period: string
      token: {
        id: string
        decimals: number
        name: string
        symbol: string
      }
    }

  }
}

const Configuration: React.FC<{
  chainId: number,
  setEnvironmentAuthorizer
}> = ({ chainId, setEnvironmentAuthorizer }) => {
  const params = useParams<Params>()
  const environment = params.id
  const { callApi } = useApi()
  const config = useEnvironmentConfig(environment, chainId)
  const tasks = config?.data?.tasks || []
  const [active, setActive] = useState<string | null>(null)
  const [updateData, setUpdateData] = useState<unknown | null>(null)
  const [configModalOpen, setConfigModalOpen] = useState(false)
  const [editedTasks, setEditedTasks] = useState<Task[]>(tasks)
  const [activeSubComponent, setActiveSubComponent] = useState<string | null>(null)

  useEffect(() => {
    if (config?.data?.authorizers) {
      setEnvironmentAuthorizer(config?.data?.authorizers[0].id)
    }
    if (tasks) {
      setEditedTasks(tasks)
    }
  }, [tasks])

  const handleInputChange = useCallback((taskId: string, field: string, value: string) => {
    setEditedTasks((prevTasks) => {
      const newTasks = [...prevTasks] // Copy the array
      const taskIndex = newTasks.findIndex((task) => task.id === taskId)
      if (taskIndex === -1) return prevTasks // If task not found, return same state

      // Handle nested field updates (e.g., "defaultTokenThreshold.min" or "defaultTokenThreshold.token.id")
      if (field.includes('.')) {
        const fieldParts = field.split('.')
        
        if (fieldParts.length === 2) {
          // Handle two-level nesting: "defaultTokenThreshold.min"
          const [parentField, childField] = fieldParts
          newTasks[taskIndex] = {
            ...newTasks[taskIndex],
            taskConfig: {
              ...newTasks[taskIndex].taskConfig,
              [parentField]: {
                ...newTasks[taskIndex].taskConfig[parentField],
                [childField]: value,
              },
            },
          }
        } else if (fieldParts.length === 3) {
          // Handle three-level nesting: "defaultTokenThreshold.token.id"
          const [parentField, middleField, childField] = fieldParts
          newTasks[taskIndex] = {
            ...newTasks[taskIndex],
            taskConfig: {
              ...newTasks[taskIndex].taskConfig,
              [parentField]: {
                ...newTasks[taskIndex].taskConfig[parentField],
                [middleField]: {
                  ...newTasks[taskIndex].taskConfig[parentField][middleField],
                  [childField]: value,
                },
              },
            },
          }
        }
      } else {
        // Handle timeLock fields (existing logic)
        newTasks[taskIndex] = {
          ...newTasks[taskIndex],
          taskConfig: {
            ...newTasks[taskIndex].taskConfig,
            timeLock: {
              ...newTasks[taskIndex].taskConfig.timeLock,
              [field]: value,
            },
          },
        }

      }

      return newTasks // Return updated state
    })
  }, [])

  const handleSave = async () => {
    try {
      const sendData = { ...config.data, tasks: editedTasks }
      const response = await callApi(
        `/environments/config/${environment}/${chainId}/compare`,
        sendData,
        'POST',
        'noMessage'
      )
      setUpdateData({
        mimicId: environment,
        currentState: config.data,
        expectedState: sendData,
        environmentUpdateTxs: response,
      })
      setConfigModalOpen(true)
    } catch (error: unknown) {
      console.error('There was an error triggering the executor job:', error)
    }
  }

  const handleToggle = (id: string) => {
    setActive((prev) => (prev === id ? null : id))
  }

  const handleSubToggle = (id: string) => {
    setActiveSubComponent((prev) => (prev === id ? null : id))
  }


  const handleCancelConfigModal = () => {
    setConfigModalOpen(false)
  }

  const handleConfirmModal = async () => {
    if (updateData) {
      try {
        // Add query parameters to filter by chainId and mimicId
        const queryParams = new URLSearchParams({
          chainId: chainId.toString(),
          mimicId: environment
        })
        const url = `/environments/updates?${queryParams.toString()}`
        
        await callApi(url, updateData, 'POST')
        //redirigir a updates
      } catch (error) {
        console.error('Error updating environment:', error)
      }
    }
    setConfigModalOpen(false)
  }

  return (
    <>
      <ConfigSection>
        <PageHeader>
          <PageTitle>Configuration Tasks</PageTitle>
          <PrimaryBtn onClick={() => handleSave()}>Save Changes</PrimaryBtn>
        </PageHeader>
        {editedTasks?.map((task) => (
          <Accordion
            key={task.id}
            active={active}
            handleToggle={handleToggle}
            id={task.id}
            header={<TaskTitle>{task.name} — <TaskId>{task.id}</TaskId></TaskTitle>}
            content={
              <TaskForm key={activeSubComponent}>
                <Accordion
                  subAccordion={true}
                  active={activeSubComponent}
                  handleToggle={handleSubToggle}
                  id={`${task.id}-timelock`}
                  header={<SectionLabel>Time Lock Configuration</SectionLabel>}
                  content={<TimeLockEdit task={task} handleInputChange={handleInputChange} />}
                />
                <Accordion
                  subAccordion={true}
                  active={activeSubComponent}
                  handleToggle={handleSubToggle}
                  id={`${task.id}-threshold`}
                  header={<SectionLabel>Token Threshold</SectionLabel>}
                  content={<DefaultTokenThresholdEdit task={task} handleInputChange={handleInputChange} />}
                />
                <Accordion
                  subAccordion={true}
                  active={activeSubComponent}
                  handleToggle={handleSubToggle}
                  id={`${task.id}-volumelimit`}
                  header={<SectionLabel>Volume Limit</SectionLabel>}
                  content={<DefaultVolumeLimitEdit task={task} handleInputChange={handleInputChange} />}
                />
              </TaskForm>
            }
          />
        ))}
      </ConfigSection>
      {configModalOpen && (
        <CustomConfirmationModal
          message="Are you sure you want to submit this change request?"
          onConfirm={handleConfirmModal}
          onCancel={handleCancelConfigModal}
          wide
        >
          {updateData && (
            <DiffViewer
              oldValue={JSON.stringify((updateData as any).currentState, null, 2)}
              newValue={JSON.stringify((updateData as any).expectedState, null, 2)}
              splitView={true}
              compareMethod={DiffMethod.WORDS}
              hideLineNumbers={true}
              showDiffOnly={true}
              useDarkTheme={true}
              leftTitle="Current"
              rightTitle="Proposed"
              styles={{
                variables: {
                  dark: {
                    diffViewerBackground: '#0f0f0f',
                    diffViewerTitleBackground: '#111',
                    diffViewerTitleColor: '#a0a0a0',
                    diffViewerTitleBorderColor: 'rgba(255,255,255,0.07)',
                    addedBackground: 'rgba(51,194,176,0.08)',
                    addedColor: '#ededed',
                    removedBackground: 'rgba(255,68,68,0.08)',
                    removedColor: '#ededed',
                    wordAddedBackground: 'rgba(51,194,176,0.25)',
                    wordRemovedBackground: 'rgba(255,68,68,0.25)',
                    addedGutterBackground: 'rgba(51,194,176,0.15)',
                    removedGutterBackground: 'rgba(255,68,68,0.15)',
                    gutterBackground: '#0d0d0d',
                    codeFoldBackground: '#111',
                    emptyLineBackground: '#0d0d0d',
                    codeFoldContentColor: '#444',
                  },
                },
              }}
            />
          )}
        </CustomConfirmationModal>
      )}
    </>
  )
}

const ConfigSection = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif;

  .show {
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
  }

  & > div > div:last-child {
    overflow: visible !important;
  }

  form > div:first-child {
    margin-top: 8px;
  }

  .flex-group {
    display: flex;
    gap: 16px;
    label {
      top: 32%;
      background: #0d0d0d;
    }
  }

  .modalContent {
    min-width: 80%;
    max-height: 80%;
    overflow-y: scroll;
  }
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

const PageTitle = styled.h2`
  color: #ededed;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
`

const TaskTitle = styled.div`
  color: #ededed;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 8px;
`

const TaskId = styled.span`
  color: #a1a1a1;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 11px;
  font-weight: 400;
`

const SectionLabel = styled.div`
  color: #a89aff;
  font-size: 13px;
  font-weight: 500;
`

const TaskForm = styled.div`display: flex; flex-direction: column; gap: 0;`

export default Configuration
